import { saveDeletionAuthProof } from '@/lib/deletion-auth-proof';
import { apiClient } from '@borradh-workspace/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';

import { resetUser } from '@/components/posthog-provider';
import { forgetActiveLocation } from '@/features/organization-locations/use-active-location';
import { clearAdminTabToken } from '@/lib/admin-tab-token';
import { clearAuthToken } from '@/lib/auth-token';
import { clearImpersonationToken } from '@/lib/impersonation-token';
import { intercomLogout } from '@/lib/intercom';
import { unregisterPushNotifications } from '@/lib/push';
import { clearSessionCache } from '@/lib/session';

interface UseSignOutOptions {
  onSuccess?: () => void;
}

export function useSignOut(options?: UseSignOutOptions) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => apiClient.post('auth/sign-out'),
    onSettled: () => {
      void intercomLogout();
      // Delete the server-side push-token row + tell the native plugin to
      // stop receiving notifications. Best-effort, server-side cleanup also
      // happens via DeviceNotRegistered receipts on next push attempt.
      void unregisterPushNotifications();
      // Detach the PostHog distinct_id so the next signed-out session isn't
      // attributed to the user who just left (esp. shared/native devices).
      resetUser();
      clearAuthToken();
      saveDeletionAuthProof(undefined);
      clearAdminTabToken();
      clearImpersonationToken();
      clearSessionCache();

      // Leave the authenticated app. Clearing local state does NOT re-run the
      // router's `beforeLoad` guards, so without this the user sat on a
      // now-unauthenticated page until something else forced a navigation.
      // Only nav-user and nav-org passed an onSuccess that navigated — the
      // header, dashboard-header and signed-in-indicator all stranded you.
      // Done here so every caller gets it; the callers that already navigate
      // to /sign-in are unaffected (same destination).
      //
      // The next account in this tab must not inherit this one's branch or
      // cached data. Sign-out does not reload the page, so both survive it:
      // the cached active org and locations list re-resolve the OLD branch,
      // and the api-client keeps sending it, and the API 404s every request
      // the new account makes.
      //
      // Clear only AFTER the dashboard has unmounted. Cleared while its queries
      // are still observed, they refetch at once without a token and the cache
      // keeps the 401s: the org list then sits in an error state with no data,
      // and the next account's onboarding drops to "Loading…" the moment it
      // creates its org — unmounting the wizard back to step 1. Forget the
      // branch after the clear: `ActiveLocationScope` re-publishes whatever
      // the cached list resolves.
      void navigate({ to: '/sign-in' }).finally(() => {
        queryClient.clear();
        forgetActiveLocation();
      });

      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      // sign-out failures still clear local state — server-side may already be invalid
      toast.error(
        error.message || 'Sign out had a problem (session cleared locally)'
      );
    },
  });

  return {
    signOut: mutation.mutate,
    isSigningOut: mutation.isPending,
  };
}
